import { useState } from "react";
import { FileUploadInput } from "@src/components/Forms/FormElements/Inputs/FileUploadInput";
import { TextAreaInput } from "@src/components/Forms/FormElements/Inputs/TextAreaInput";
import { OrSectionSeparator } from "@src/components/Forms/FormElements/OrSectionSeparator";
import { generate_random_color } from "#shared/utility_functions/color_functions.js";
import { ShowHideSection } from "@src/components/Forms/FormElements/ShowHideSection";
import { TextInput } from "@src/components/Forms/FormElements/Inputs/TextInput";
import { ColorInputs } from "@src/components/Forms/FormElements/Inputs/ColorInputs";
import { PublishStatusSelectInput } from "@src/components/Forms/FormElements/Inputs/PublishStatusSelectInput";
import { validateInput } from "@src/utility_functions/inputValidation/validateInput";
import { ImportRowInstructions } from "@src/components/Forms/Rows/ImportRow/Custom/ImportRowInstructions";
import { timelineFormatExample } from "@src/components/Static/timelineFormatExample";
import {
  validateLabel,
  validateDescription,
} from "#shared/inputValidation/inputValidation.js";
import { FileUploadRules } from "@src/components/Forms/Rows/ImportRow/FileUploadRules";
import { createRowWithIntervals } from "@src/utility_functions/database_operations/rows/createRowWithIntervals";
import { WaitMessage } from "@src/components/Forms/Rows/ImportRow/WaitMessage";
import { validatePastedJSON } from "@src/utility_functions/inputValidation/validatePastedJSON";
import type { ProcessedData } from "@src/types/timeline";
import { useQueryClient } from "@tanstack/react-query";
import { CHART_TYPE_TIMELINE } from "#shared/constants/chartTypes.js";
import { useModal } from "@src/utility_functions/customHooks/useModal";

export const ImportRowCustom = (props) => {
  const { closeDialog } = useModal();
  const queryClient = useQueryClient();
  const [submitButtonDisabled, setSubmitButtonDisabled] = useState(true);
  const [processedData, setProcessedData] = useState<ProcessedData | null>(
    null,
  );
  // Same no-verdict-yet shape the other forms use (was a bare "").
  const [labelError, setLabelError] = useState({
    errorClass: null,
    errorMessages: [],
  });
  const [descriptionError, setDescriptionError] = useState(null);
  const default_primaryColor = generate_random_color("color");
  const default_secondaryColor = generate_random_color("shade");
  const [primaryColorValue, setPrimaryColorValue] =
    useState(default_primaryColor);
  const [secondaryColorValue, setSecondaryColorValue] = useState(
    default_secondaryColor,
  );
  const [fileName, setFileName] = useState(null);
  const [pasteError, setPasteError] = useState({
    errorClass: "default",
    errorMessages: [""],
  });
  const [fileUploadDisabled, setFileUploadDisabled] = useState(false);
  const [textAreaDisabled, setTextAreaDisabled] = useState(false);
  const fileSizeLimitMBs = 10;

  const submitHandler = async (event: React.FormEvent<HTMLFormElement>) => {
    props.setSaveButtonLoading(true);
    event.preventDefault();
    event.stopPropagation();
    const formData = new FormData(event.currentTarget);
    const formDataEntries = Object.fromEntries(formData.entries());
    props.setSaveButtonLoading(true);

    const serverPayload = {
      label: formDataEntries.label || fileName || "New Row",
      description: formDataEntries.description,
      primaryColor: formDataEntries.primaryColor || "blue",
      secondaryColor: formDataEntries.secondaryColor || "500",
      publishStatus: formDataEntries.publishStatus || "published",
      intervals: processedData
        ? JSON.stringify(processedData?.intervals)
        : formDataEntries.importCustomRowJSONInput,
      chartType: props.chartType,
      chartID: props.chartID,
      source: "customImport",
    };
    const serverResponse = await createRowWithIntervals(serverPayload);
    const newRowID = serverResponse.payload.newRowID;
    if (serverResponse.error) {
      console.warn(serverResponse.error);
      setPasteError(serverResponse);
      props.setSaveButtonLoading(false);
    } else if (serverResponse.payload.chartType === CHART_TYPE_TIMELINE) {
      queryClient.invalidateQueries({
        queryKey: ["timelineDocument", props.ownerID, props.chartID],
      });
      closeDialog();
    } else {
      window.location.href =
        "/user/" +
        props.loggedInUser?.id +
        "/" +
        props.chartType +
        "/" +
        newRowID;
    }
  };

  return (
    <>
      <div className={props.customClass}>
        <form
          id="importCustomJSONForm"
          noValidate={true}
          onSubmit={(event) => {
            submitHandler(event);
          }}
        >
          <div className="two_column_gutter_2 _margin-right--2rem">
            <TextAreaInput
              className="_margin-bottom--1rem"
              inputId="importCustomRowJSONInput"
              name="importCustomRowJSONInput"
              labelText="Paste JSON"
              maxlength={100000}
              defaultValue={props.chartDescription}
              placeholder={timelineFormatExample}
              required={true}
              validationResponse={pasteError}
              isDisabled={textAreaDisabled}
              onChangeFunction={(event) => {
                setPasteError(validatePastedJSON(event.target.value));
                if (event.target.value.length > 0) {
                  setFileUploadDisabled(true);
                } else {
                  setFileUploadDisabled(false);
                }
              }}
            />
            <OrSectionSeparator case="lower" className="_margin-bottom--1rem" />
            <FileUploadInput
              type="custom"
              fileSizeLimitMBs={fileSizeLimitMBs}
              loggedInUser={props.loggedInUser}
              isLoading={props.saveButtonLoading}
              setIsLoading={props.setSaveButtonLoading}
              submitButtonDisabled={submitButtonDisabled}
              setSubmitButtonDisabled={setSubmitButtonDisabled}
              setProcessedData={setProcessedData}
              fileName={fileName}
              setFileName={setFileName}
              fileUploadDisabled={fileUploadDisabled}
              setTextAreaDisabled={setTextAreaDisabled}
              setFileUploadDisabled={setFileUploadDisabled}
            />
            <FileUploadRules
              fileSizeLimitMBs={fileSizeLimitMBs}
              allowedFileTypes={[".csv", ".json"]}
            />
          </div>
          <div className="two_column_gutter_2 _font-size--10pt _margin-top--1rem">
            <ImportRowInstructions formatExample={timelineFormatExample} />
          </div>

          <ShowHideSection
            linkText="Timeline Metadata"
            parantheticalText="(Optional)"
            linkColor="blue"
          >
            <>
              <p className="_font-size--11pt _margin-bottom--1rem _margin-top--1rem">
                These fields are optional. If you leave them blank they will be
                filled in with default values. You can always update the value
                of these fields later.
              </p>
              <TextInput
                className="_margin-bottom--1rem"
                inputId="importCustomRowLabelInput"
                name="label"
                inputType="text"
                labelText="Label"
                maxlength={50}
                defaultValue=""
                placeholder=""
                autocomplete=""
                required={true}
                validationResponse={labelError}
                onChangeFunction={(event) => {
                  validateInput(
                    event.target.value,
                    validateLabel,
                    setLabelError,
                  );
                }}
              />
              <TextAreaInput
                className="_margin-bottom--1rem"
                inputId="importCustomRowDescriptionInput"
                name="description"
                inputType="description"
                labelText="Description"
                maxlength={1000}
                defaultValue=""
                placeholder="Description is optional"
                required={true}
                validationResponse={descriptionError}
                onChangeFunction={(event) => {
                  validateInput(
                    event.target.value,
                    validateDescription,
                    setDescriptionError,
                  );
                }}
              />
              <ColorInputs
                primaryColorValue={primaryColorValue}
                secondaryColorValue={secondaryColorValue}
                setPrimaryColorValue={setPrimaryColorValue}
                setSecondaryColorValue={setSecondaryColorValue}
              />
              <PublishStatusSelectInput
                publishStatus="published"
                inputID="importCustomRowPublishStatusInput"
              />
            </>
          </ShowHideSection>
          {props.saveButtonLoading && (
            <>
              <WaitMessage />
            </>
          )}
        </form>
      </div>
    </>
  );
};
